import { useContext, useEffect, useState } from 'react'
import { Box, Link, Stack, Typography } from '@mui/material'
import { InfoOutlined } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { ReportPeriodContext } from '../../../context/report-periods/report-periods'
import { UserDetailsContext } from '../../../context/user-details/user-details'
import RegulationApplicabilityService from '../../../shared/services/regulation-applicability/regulation-applicability.service'
import { IRegulationApplicabilityResponse, IRegulationResult } from '../../../shared/services/regulation-applicability/regulation-applicability.model'
import { DialogWrapper } from '../display-elements-components/dialog-wrapper'
import { criterionIcon, formatValue, statusChip } from './regulation-status'

// Which UK regulations the organisation falls into, evaluated from the current reporting
// period's figures. Sits above the report cards on the Reporting page; hides itself for
// roles or states where the evaluation is unavailable.
export const RegulationApplicabilityCard = () => {
  const navigate = useNavigate()
  const reportPeriodContext = useContext(ReportPeriodContext)
  const userDetailsContext = useContext(UserDetailsContext)
  const [response, setResponse] = useState<IRegulationApplicabilityResponse | null>(null)
  const [selectedRegulation, setSelectedRegulation] = useState<IRegulationResult | null>(null)
  const regulationApplicabilityService = new RegulationApplicabilityService()

  const reportPeriodId = reportPeriodContext.lastSelectedReportId
  const isAdmin = userDetailsContext.userDetails?.roles?.includes('admin') ?? false

  useEffect(() => {
    if (!reportPeriodId) {
      setResponse(null)
      return
    }
    // Stale-response guard: a period switch mid-flight must not paint the old period's answer.
    let cancelled = false
    regulationApplicabilityService.getForPeriod(reportPeriodId)
      .then(result => { if (!cancelled) setResponse(result) })
      .catch(() => { if (!cancelled) setResponse(null) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportPeriodId])

  if (!response) return null

  const missingCompanyType = !response.inputs.companyType
  // Financial thresholds are GBP; prompting for figures on a non-GBP period would collect
  // numbers the evaluation deliberately ignores.
  const nonGbpPeriod = !!response.currencyCode && response.currencyCode.toUpperCase() !== 'GBP'
  const missingFinancials = !nonGbpPeriod && (response.inputs.totalRevenue == null || response.inputs.balanceSheetTotal == null)
  const missingEmployees = response.inputs.employeesTotal == null

  return (
    <Box sx={{ borderWidth: '1px', borderStyle: 'solid', borderColor: 'lightGrey.main', borderRadius: '10px', p: 3, mb: 4 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent='space-between' spacing={2}>
        <Box>
          <Typography variant='subtitle1' sx={{ color: 'text.primary' }}>Applicable regulations</Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Based on this reporting period&apos;s figures. Select a regulation for the detail, or{' '}
            <Link component='button' type='button' onClick={() => navigate('/reporting/regulations?tab=regulations')} sx={{ verticalAlign: 'baseline' }}>
              view the full breakdown
            </Link>.
          </Typography>
        </Box>
        <Stack direction='row' spacing={2} flexWrap='wrap' useFlexGap>
          {response.regulations.map(regulation => (
            <Stack
              key={regulation.key}
              direction='row'
              alignItems='center'
              spacing={1}
              onClick={() => setSelectedRegulation(regulation)}
              sx={{ cursor: 'pointer' }}
            >
              <Typography variant='body2' sx={{ color: 'text.primary' }}>{regulation.shortLabel}</Typography>
              {statusChip(regulation.status)}
            </Stack>
          ))}
        </Stack>
      </Stack>
      {(missingCompanyType || missingFinancials || missingEmployees) && (
        <Stack direction='row' alignItems='center' spacing={1} sx={{ mt: 2 }}>
          <InfoOutlined fontSize='small' sx={{ color: 'text.secondary' }} />
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {/* Only admins can reach the settings pages; everyone else gets redirected copy. */}
            {!isAdmin && 'Ask an administrator to complete your company type, employee counts and financial figures to sharpen these results.'}
            {isAdmin && missingCompanyType && (
              <>
                <Link component='button' type='button' onClick={() => navigate('/settings/company-settings/overview')} sx={{ verticalAlign: 'baseline' }}>
                  Set your company type
                </Link>
                {missingFinancials || missingEmployees ? ' and ' : ' to sharpen these results.'}
              </>
            )}
            {isAdmin && missingFinancials && (
              <>
                <Link component='button' type='button' onClick={() => navigate(`/settings/company-settings/reporting-periods/${response.reportPeriodId}`)} sx={{ verticalAlign: 'baseline' }}>
                  {missingCompanyType ? 'add' : 'Add'} this period&apos;s revenue and balance sheet total
                </Link>
                {missingEmployees ? ' and ' : ' to sharpen these results.'}
              </>
            )}
            {isAdmin && missingEmployees && (
              <>
                <Link component='button' type='button' onClick={() => navigate(`/settings/company-settings/reporting-periods/${response.reportPeriodId}`)} sx={{ verticalAlign: 'baseline' }}>
                  {missingCompanyType || missingFinancials ? 'enter' : 'Enter'} your employee counts
                </Link>
                {' to sharpen these results.'}
              </>
            )}
          </Typography>
        </Stack>
      )}
      <DialogWrapper
        open={!!selectedRegulation}
        onClose={() => setSelectedRegulation(null)}
        title={selectedRegulation?.label || ''}
        cancelHandler={() => setSelectedRegulation(null)}
        submitHandler={() => setSelectedRegulation(null)}
        isSubmitting={false}
        hideCancelButton={true}
        submitButtonText='Close'
        maxWidth='sm'
      >
        {selectedRegulation && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction='row' alignItems='center' spacing={1}>
              {statusChip(selectedRegulation.status)}
              {selectedRegulation.status === 'applies' && response.reportingDeadline && (
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  Reporting deadline you set: {new Date(response.reportingDeadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                </Typography>
              )}
            </Stack>
            <Box>
              {selectedRegulation.reasons.map(reason => (
                <Stack key={reason.criterion} direction='row' alignItems='center' spacing={1.5} sx={{ py: 0.75, borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: 'lightGrey.main' }}>
                  {criterionIcon(reason.met)}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant='body2' sx={{ color: 'text.primary' }}>{reason.label}</Typography>
                    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                      Your figure: {formatValue(reason.criterion, reason.value)}
                      {reason.note ? ` (${reason.note})` : ''}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Box>
            {selectedRegulation.notes.map(note => (
              <Stack key={note} direction='row' spacing={1} alignItems='flex-start'>
                <InfoOutlined fontSize='small' sx={{ color: 'text.secondary', mt: '2px' }} />
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>{note}</Typography>
              </Stack>
            ))}
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              This is guidance based on the figures you have entered, not legal advice.
              {' '}
              <Link href={selectedRegulation.infoUrl} target='_blank' rel='noopener noreferrer'>
                Read the official guidance
              </Link>
            </Typography>
          </Stack>
        )}
      </DialogWrapper>
    </Box>
  )
}
